/**
 * Solicitações — o que a equipe pediu pelo botão de feedback, aberto para todos.
 *
 * **Para quem pediu, não para quem resolve.** A fila de trabalho continua em Feedbacks
 * (no "Mais", com a chave de relatórios), com o botão de resolver. Aqui é só leitura e
 * responde duas perguntas:
 *
 * - **"o meu já foi feito?"** — "Só as minhas" mostra os pedidos da pessoa, com as
 *   contagens dela, e cada um diz se está em aberto ou feito, por quem e quando;
 * - **"alguém já pediu isso?"** — a busca procura na mensagem de todos, por palavra, sem
 *   caixa e sem acento. Sem resultado, o próprio vazio oferece mandar o pedido, com o
 *   texto procurado já no campo.
 *
 * O navegador de quem escreveu não aparece: só interessa a quem vai reproduzir o problema.
 */

import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  abrirFeedback, listarFeedbacksDoSistema, type CodigoDoTipo, type Situacao,
} from "@/api/feedbackDoSistema"
import { BUSCA_ESPERA_MS } from "@/api/query"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Faixa } from "@/kit/Faixa"
import { D, Icone } from "@/casca/iconesDoFeedback"
import { usarSessao } from "@/sessao/Sessao"
import { Botao, estiloCampo, mensagem } from "../outros/pecas"
import { CartaoDoFeedback, Esqueleto, Galeria, Segmentado } from "./FeedbackDaEquipe"

type Escopo = "todas" | "minhas"

const TIPOS: { codigo: CodigoDoTipo; plural: string }[] = [
  { codigo: "problema", plural: "Problemas" },
  { codigo: "ideia", plural: "Ideias" },
  { codigo: "elogio", plural: "Elogios" },
]

export function Solicitacoes() {
  const { painelId } = usarSessao()
  const [escopo, setEscopo] = useState<Escopo>("todas")
  const [situacao, setSituacao] = useState<Situacao>("todos")
  const [tipo, setTipo] = useState<CodigoDoTipo | "">("")
  const [texto, setTexto] = useState("")
  const [busca, setBusca] = useState("")
  const [pagina, setPagina] = useState(1)
  const [galeria, setGaleria] = useState<{ ids: number[]; indice: number } | null>(null)

  // A busca vai ao servidor quando a digitação para — e volta à primeira página.
  useEffect(() => {
    const t = setTimeout(() => { setBusca(texto.trim()); setPagina(1) }, BUSCA_ESPERA_MS)
    return () => clearTimeout(t)
  }, [texto])

  const meus = escopo === "minhas"
  const dados = useQuery({
    // O painel entra na chave: "só as minhas" é uma resposta diferente para cada pessoa.
    queryKey: ["feedback-sistema", "solicitacoes", meus ? painelId : "todos", tipo, situacao, busca, pagina] as const,
    queryFn: () => listarFeedbacksDoSistema({ tipo, situacao, pagina, busca, meus }),
    placeholderData: (anterior) => anterior,
  })

  const contagem = dados.data?.contagem
  const itens = dados.data?.itens ?? []
  const paginas = dados.data?.totalPaginas ?? 0
  const achados = dados.data?.totalNoFiltro ?? 0
  const mudar = (f: () => void) => { f(); setPagina(1) }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Solicitações"
        detalhe={dados.data ? `${achados} no filtro` : undefined}
      >
        <Segmentado
          valor={escopo}
          aoMudar={(v) => mudar(() => setEscopo(v))}
          opcoes={[
            { valor: "todas", rotulo: "De todos" },
            { valor: "minhas", rotulo: "Só as minhas" },
          ]}
        />
        <Segmentado
          valor={situacao}
          aoMudar={(v) => mudar(() => setSituacao(v))}
          opcoes={[
            { valor: "abertos", rotulo: "Em aberto", conta: contagem?.abertos },
            { valor: "resolvidos", rotulo: "Feitas", conta: contagem?.resolvidos },
            { valor: "todos", rotulo: "Todas", conta: contagem?.total },
          ]}
        />
        <select
          value={tipo}
          onChange={(e) => mudar(() => setTipo(e.target.value as CodigoDoTipo | ""))}
          style={{ ...estiloCampo, width: 150 }}
          aria-label="Tipo"
        >
          <option value="">Todos os tipos</option>
          {TIPOS.map((t) => <option key={t.codigo} value={t.codigo}>{t.plural}</option>)}
        </select>
        <Botao destaque aoClicar={() => abrirFeedback()}>Nova solicitação</Botao>
      </Faixa>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 18px 90px" }}>
        <div style={{ maxWidth: 920, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
          <label style={{
            display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 12,
            background: "var(--surface)", border: "1.5px solid var(--line)", boxShadow: "var(--sombra)",
          }}>
            <span aria-hidden style={{ color: "var(--txt3)", display: "flex" }}>
              <Icone d={D.balao} tamanho={18} />
            </span>
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Antes de pedir, procure: alguém já pediu isso?"
              aria-label="Procurar nas solicitações"
              style={{
                flex: 1, minWidth: 0, border: 0, outline: "none", background: "transparent",
                fontSize: 14, color: "var(--txt)",
              }}
            />
            {texto && (
              <button type="button" onClick={() => setTexto("")} aria-label="Limpar a busca"
                      style={{ color: "var(--txt3)", fontSize: 16, lineHeight: 1 }}>×</button>
            )}
          </label>

          {busca && !dados.isPending && achados > 0 && (
            <p style={{ margin: "-4px 2px 0", fontSize: 12, color: "var(--txt2)", lineHeight: 1.55 }}>
              <b>{achados}</b> {achados === 1 ? "pedido parecido" : "pedidos parecidos"} com "{busca}".
              Se o seu é o mesmo, não precisa pedir de novo — acompanhe por aqui.
            </p>
          )}

          {dados.isPending ? (
            <Esqueleto />
          ) : dados.error ? (
            <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(dados.error)}</p>
          ) : itens.length === 0 ? (
            <Vazio busca={busca} meus={meus} />
          ) : (
            itens.map((f) => (
              <CartaoDoFeedback
                key={f.id}
                f={f}
                selo={f.painelId === painelId ? <Seu /> : undefined}
                aoAbrirImagem={(indice) => setGaleria({ ids: f.imagens.map((i) => i.id), indice })}
              />
            ))
          )}

          {paginas > 1 && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}>
              <Botao aoClicar={() => setPagina((p) => p - 1)} desligado={pagina <= 1}>Anterior</Botao>
              <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>página {pagina} de {paginas}</span>
              <Botao aoClicar={() => setPagina((p) => p + 1)} desligado={pagina >= paginas}>Próxima</Botao>
            </div>
          )}
        </div>
      </div>

      <BarraDeEstado direita={contagem ? `${contagem.abertos} em aberto · ${contagem.resolvidos} feita(s)` : undefined}>
        {meus
          ? "Os pedidos que você mandou pelo botão de feedback."
          : "O que a equipe pediu pelo botão de feedback — de todos, para ninguém pedir duas vezes."}
      </BarraDeEstado>

      {galeria && (
        <Galeria ids={galeria.ids} inicial={galeria.indice} aoFechar={() => setGaleria(null)} />
      )}
    </div>
  )
}

function Seu() {
  return (
    <span style={{
      height: 18, padding: "0 7px", borderRadius: 999, display: "inline-flex", alignItems: "center",
      background: "var(--brand-soft)", color: "var(--brand-on-soft)", fontSize: 10.5, fontWeight: 700,
    }}>
      seu
    </span>
  )
}

function Vazio({ busca, meus }: { busca: string; meus: boolean }) {
  const titulo = busca
    ? `Ninguém pediu "${busca}" ainda.`
    : meus ? "Você ainda não mandou nenhuma solicitação." : "Nenhuma solicitação neste filtro."

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "44px 16px",
      borderRadius: 12, border: "1.5px dashed var(--line2)", color: "var(--txt3)", textAlign: "center",
    }}>
      <span style={{
        width: 48, height: 48, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
        background: "var(--surface3)", color: "var(--txt2)",
      }}>
        <Icone d={D.balao} tamanho={24} />
      </span>
      <strong style={{ fontSize: 13.5, color: "var(--txt)" }}>{titulo}</strong>
      <span style={{ fontSize: 12.5, maxWidth: "50ch", lineHeight: 1.55 }}>
        {busca
          ? "Confira com outras palavras — ou mande o pedido, e ele aparece aqui para todos acompanharem."
          : "Peça pelo botão de feedback, no canto de baixo de qualquer tela."}
      </span>
      <Botao destaque aoClicar={() => abrirFeedback(busca ? { tipo: "ideia", texto: busca } : undefined)}>
        {busca ? "Pedir isso" : "Nova solicitação"}
      </Botao>
    </div>
  )
}
